"use client"

import { useTranslations } from "next-intl"
import type { ReactNode } from "react"
import { GitForkIcon, StarIcon, TimerIcon } from "~/components/ui/icons"
import { cx, formatCompactNumber, formatRelativeDate } from "~/lib/legacy-utils"

type RepositoryStats = {
  stars: number
  forks: number
  pushedAt: Date | string | null
}

type ToolRepositoryStatsProps = {
  repository: RepositoryStats
  className?: string
}

const ToolRepositoryStats = ({ repository, className }: ToolRepositoryStatsProps) => {
  const t = useTranslations("tools.repository")

  return (
    <ul className={cx("w-full text-xs mt-auto", className)}>
      <StatRow
        icon={<StarIcon />}
        label={t("stars")}
        value={formatCompactNumber(repository.stars)}
      />
      <StatRow
        icon={<GitForkIcon />}
        label={t("forks")}
        value={formatCompactNumber(repository.forks)}
      />
      {repository.pushedAt && (
        <StatRow
          icon={<TimerIcon />}
          label={t("last_commit")}
          value={formatRelativeDate(repository.pushedAt)}
          suppressHydrationWarning
        />
      )}
    </ul>
  )
}

function StatRow({
  icon,
  label,
  value,
  suppressHydrationWarning,
}: Readonly<{
  icon: ReactNode
  label: string
  value: ReactNode
  suppressHydrationWarning?: boolean
}>) {
  return (
    <li className="flex items-start gap-3 py-1">
      <p className="flex items-start min-w-0 gap-1.5 text-secondary-foreground">
        <span className="size-[1.1em] shrink-0 opacity-75 [&>svg]:size-full">{icon}</span>
        <span className="flex-1 truncate">{label}</span>
      </p>
      <span className="min-w-2 flex-1 border-b border-dotted border-secondary-foreground/50" />
      <span
        className="shrink-0 font-medium tabular-nums"
        suppressHydrationWarning={suppressHydrationWarning}
      >
        {value}
      </span>
    </li>
  )
}

export { ToolRepositoryStats, type RepositoryStats }
